import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ToastContainer, toast } from "react-toastify";
import Cookies from 'js-cookie';
import axios from "axios";
import { url } from '../../../../../../../../../App';
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import "./Expenses.css";

const BusinessExpense = ({ loanId, visible, addExpense, editExpense, deleteExpense, isDisbursed }) => {
    const [open, setOpen] = React.useState(false);
    const token = Cookies.get('Token');
    const email = Cookies.get('Email');
    const [businessExpenses, setBusinessExpenses] = useState([
        { head: '', monthly: "", remarks: '' },
    ]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (!loanId) return;
        const fetchExpenses = async () => {
            setOpen(true);
            try {
                const response = await axios.post(url + '/cam/get/business/expense', {
                    token,
                    email,
                    loanId
                });

                if (response.data.auth) {
                    const fetchedExpenses = response.data.expenses;

                    if (fetchedExpenses && fetchedExpenses.length > 0) {
                        setBusinessExpenses(fetchedExpenses);
                        setIsSubmitted(true);
                        setEditingRowIndex(null);
                    } else {
                        // If no data in DB, then show one empty row
                        setBusinessExpenses([
                            { head: '', monthly: "", remarks: '' }
                        ]);
                        setIsSubmitted(false);
                    }
                }
                else {
                    setBusinessExpenses([
                        { head: '', monthly: "", remarks: '' }
                    ]);
                    setIsSubmitted(false);
                }
            } catch (err) {
                console.error('Error fetching expenses:', err);
            } finally {
                setOpen(false);
            }
        };

        fetchExpenses();
    }, [loanId, email, token]);

    const handleChange = (index, field, value) => {
        const newExpenses = [...businessExpenses];
        newExpenses[index][field] = value;
        setBusinessExpenses(newExpenses);
    };

    const addRow = () => {
        const newIndex = businessExpenses.length;
        setBusinessExpenses([
            ...businessExpenses,
            { head: '', monthly: '', remarks: '' },
        ]);
        setEditingRowIndex(newIndex);
        setIsEditMode(false);
    };

    const handleDelete = async (expenseId, index) => {
        // console.log(expenseId)
        const confirmDelete = window.confirm("Are you sure you want to delete this expense?");
        if (!confirmDelete) return;

        // Case 1: No expenseId → Just remove empty rows from frontend
        if (!expenseId) {
            setBusinessExpenses(businessExpenses?.filter((_, i) => i !== index));
            toast.success("Empty row removed.");
            return;
        }
        // Case 2: expenseId exists → Delete from DB
        try {
            setOpen(true);
            const response = await axios.post(url + '/cam/delete/business/expense', {
                token,
                email,
                loanId,
                expenseId
            });

            if (response.data.auth) {
                setBusinessExpenses(response.data.data);
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Delete error:', error);
        } finally {
            setOpen(false);
        }
    };

    const handleSubmit = async () => {
        // console.log(businessExpenses);
        if (!businessExpenses || businessExpenses.length === 0) {
            toast.error("Please add at least one Business Expense before submitting.");
            return;
        }
        const isAnyHeadEmpty = businessExpenses?.some(exp => exp.head.trim() === '');
        if (isAnyHeadEmpty) {
            toast.error("Expense Head field cannot be empty.");
            return;
        }
        setOpen(true);
        try {
            const response = await axios.post(url + '/cam/add/business/expense', {
                token,
                email,
                loanId: loanId,
                business_expenses: businessExpenses
            });
            if (response.data.auth) {
                setBusinessExpenses(response.data.data);
                setIsSubmitted(true);
                setEditingRowIndex(null);
                setIsEditMode(false);
                toast.success(response.data.message)
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Error fetching CAM data:", error);
        } finally {
            setOpen(false);
        }
    };

    const totalMonthly = businessExpenses?.reduce((sum, exp) => sum + Number(exp.monthly || 0), 0);

    const businessOptions = [
        "Labour/Staff Salary",
        "Shop Exp (rent)",
        "Travelling Exp",
        "Shop Electricity Bill ",
        "Petrol and Mobile Exp",
    ];

    return (
        <div className='main-container'>
            {loanId && (
                <>
                    <h3 style={{ textAlign: "center" }}>Business Expense</h3>
                    <div className="expense-container">
                        <table border="1" cellPadding="5" style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#cfe2f3' }}>
                                    <th>Sr. no</th>
                                    <th>Expense Head*</th>
                                    <th>Monthly (₹)</th>
                                    <th>Remarks</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {businessExpenses?.map((exp, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <select
                                                value={exp.head}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                                onChange={(e) => handleChange(index, 'head', e.target.value)}
                                            >
                                                <option value="">Select Expense</option>
                                                {businessOptions?.map((option, i) => (
                                                    <option key={i} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                value={exp.monthly}
                                                onChange={(e) => handleChange(index, 'monthly', e.target.value)}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>
                                            <textarea
                                                value={exp.remarks}
                                                onChange={(e) => handleChange(index, 'remarks', e.target.value)}
                                                rows={2}
                                                style={{ width: '90%' }}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>
                                            {(deleteExpense || visible) && !isDisbursed && (
                                                <Tooltip title="Delete Expense">
                                                    <IconButton
                                                        onClick={() => handleDelete(exp._id, index)}
                                                    >
                                                        <DeleteIcon sx={{ color: 'red' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(editExpense || visible) && !isDisbursed && (
                                                <Tooltip title="Edit Expense">
                                                    <IconButton
                                                        onClick={() => {
                                                            setEditingRowIndex(index);
                                                            setIsEditMode(true);
                                                        }}
                                                    >
                                                        <EditIcon sx={{ color: '#034c9b' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                <tr style={{ backgroundColor: '#e2f0d9' }}>
                                    <td colSpan="2"><strong>Total Monthly Expenses</strong></td>
                                    <td colSpan="1"><strong>{totalMonthly}</strong></td>
                                    <td colSpan="2"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {(addExpense || visible) && !isDisbursed && (
                        <button type="submit" className="expense-btn" onClick={addRow}>
                            Add Row
                        </button>
                    )}
                    {(addExpense || visible) && !isDisbursed && (
                        <button type="submit" className="expense-btn" onClick={handleSubmit}>
                            {isEditMode ? 'Update' : 'Submit'}
                        </button>
                    )}
                </>
            )}
            <ToastContainer />
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={open}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
};

export default BusinessExpense;